import { fail } from './errors.js';

/* ---------------------------------------------------------------------------
 * 본사 운영: 유료계약(요금제·모듈) 신청·심사, 알림톡 신청·승인, 공지, 1:1 문의
 * ------------------------------------------------------------------------- */

export const PLANS = ['TRIAL', 'STANDARD', 'ENTERPRISE_PRO'] as const;
export type PlanCode = (typeof PLANS)[number];
export const MODULES = ['SCHEDULE', 'BILLING', 'MESSAGING', 'MONITORING', 'TASKS', 'AI', 'PAYROLL', 'CLOSING'] as const;
export type ModuleCode = (typeof MODULES)[number];

/**
 * 월 요금(원, 부가세 포함). 금액은 서버가 계산한다 — 신청 화면이 보낸 금액은 믿지 않는다.
 * 저장된 값을 옮기지 않으려고 코드는 그대로 두었다. STANDARD 는 화면에서 Basic, ENTERPRISE_PRO 는 Pro 다.
 */
export const PLAN_PRICES: Record<Exclude<PlanCode, 'TRIAL'>, number> = { STANDARD: 49_000, ENTERPRISE_PRO: 99_000 };
/** 요금제별 선생님(재직 중인 치료사) 수 한도. 넘으면 별도 문의다. 무료체험에는 없다. */
export const PLAN_TEACHER_LIMIT: Partial<Record<PlanCode, number>> = { STANDARD: 5, ENTERPRISE_PRO: 15 };
export const TRIAL_DAYS = 60;
/** 센터당 한 달 AI 호출 한도(초안·전사 합계). */
export const AI_MONTHLY_LIMIT = 200;
/** 알림톡 1건에 쓰는 포인트(1P = 1원). */
export const MESSAGE_POINTS = 15;
/** 연간 결제는 12개월을 쓰고 10개월치를 낸다. */
export const YEARLY_PAID_MONTHS = 10;
/** 초기 센터 혜택: 선착순 센터 수, 할인율(%), 기간(개월) */
export const FOUNDING_LIMIT = 10;
export const FOUNDING_PERCENT = 50;
export const FOUNDING_MONTHS = 12;

/** 요금제에 포함된 모듈. 무료체험은 체험 기간 동안 모든 기능을 쓴다. */
export const PLAN_MODULES: Record<PlanCode, ModuleCode[]> = {
  TRIAL: [...MODULES],
  STANDARD: ['SCHEDULE', 'TASKS', 'MONITORING', 'BILLING', 'MESSAGING'],
  ENTERPRISE_PRO: ['SCHEDULE', 'TASKS', 'MONITORING', 'BILLING', 'MESSAGING', 'AI', 'PAYROLL', 'CLOSING'],
};
/** 체험이 끝났거나 요금제가 끝난 센터가 계속 쓰는 기능 */
export const LIMITED_MODULES: ModuleCode[] = ['SCHEDULE', 'TASKS'];

export type BillingCycle = 'MONTHLY' | 'YEARLY';
export const cycleMonths = (c: BillingCycle | undefined) => (c === 'YEARLY' ? 12 : 1);

interface PlanState { plan: PlanCode; modules: ModuleCode[]; trialEndsAt?: string }
/**
 * 지금 쓸 수 있는 모듈. 무료체험은 끝나는 시각을 따로 지우지 않고 읽을 때 판단한다.
 * 유료 요금제는 저장된 값이 예전 것이어도 그 요금제의 모듈을 모두 쓴다.
 */
export function activeModules(c: PlanState, now: string): ModuleCode[] {
  if (c.plan !== 'TRIAL') return MODULES.filter((m) => PLAN_MODULES[c.plan].includes(m) || c.modules.includes(m));
  if (c.trialEndsAt && now < c.trialEndsAt) return c.modules;
  return c.modules.filter((m) => LIMITED_MODULES.includes(m));
}
export const hasModule = (c: PlanState, m: ModuleCode, now: string) => activeModules(c, now).includes(m);
export const trialEnd = (now: string) => new Date(Date.parse(now) + TRIAL_DAYS * 86_400_000).toISOString();

export type DecisionStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface ContractRequest {
  id: string;
  centerId: string;
  centerName: string;
  plan: Exclude<PlanCode, 'TRIAL'>;
  addModules: ModuleCode[];
  monthlyFee: number;
  /** 없으면 월 결제 */
  cycle?: BillingCycle;
  /** 정기결제 수단 표시용 이름(예: "법인카드 끝자리 1234"). 카드번호 전체는 받지 않는다 — 결제는 PG 연동 단계에서 */
  paymentLabel: string;
  status: DecisionStatus;
  requestedBy: string;
  requestedAt: string;
  decidedBy?: string;
  decidedAt?: string;
  rejectReason?: string;
  version: number;
}

/** 결제 한 번의 금액 */
export const cycleFee = (plan: Exclude<PlanCode, 'TRIAL'>, cycle: BillingCycle | undefined) => PLAN_PRICES[plan] * (cycle === 'YEARLY' ? YEARLY_PAID_MONTHS : 1);

/** 결제수단 이름에 카드번호처럼 보이는 긴 숫자가 있으면 거부한다(민감 결제정보를 저장하지 않기 위해). */
export function validatePaymentLabel(label: string): string {
  const v = label.trim();
  if (v.length < 2 || v.length > 40) fail('VALIDATION', '결제수단 이름은 2~40자로 입력하세요. 예: 법인카드 끝자리 1234');
  if (/\d[\d\s-]{6,}\d/.test(v)) fail('VALIDATION', '카드번호·계좌번호 전체는 입력하지 마세요. 끝자리 4자리만 적으면 됩니다.');
  return v;
}

export function newContractRequest(i: {
  id: string; center: { id: string; name: string }; plan: Exclude<PlanCode, 'TRIAL'>; cycle?: BillingCycle;
  paymentLabel: string; by: string; now: string; hasPending: boolean;
}): ContractRequest {
  if (i.hasPending) fail('CONFLICT', '심사 중인 신청이 있습니다. 본사 심사가 끝난 뒤 다시 신청하세요.');
  if (!(i.plan in PLAN_PRICES)) fail('VALIDATION', '요금제를 고르세요.');
  return {
    id: i.id, centerId: i.center.id, centerName: i.center.name, plan: i.plan, addModules: [], cycle: i.cycle ?? 'MONTHLY',
    monthlyFee: PLAN_PRICES[i.plan], paymentLabel: validatePaymentLabel(i.paymentLabel),
    status: 'PENDING', requestedBy: i.by, requestedAt: i.now, version: 1,
  };
}

/**
 * 계약 승인: 신청한 요금제와 모듈만 반영한다.
 * (기존 데모는 신청 상품과 상관없이 무조건 Enterprise Pro + 수납으로 바꾸는 버그가 있었다.)
 */
export function approveContract<C extends { plan: PlanCode; status: string; modules: ModuleCode[] }>(center: C, req: ContractRequest, by: string, now: string) {
  if (req.status !== 'PENDING') fail('CONFLICT', '이미 처리된 신청입니다.');
  // 새 요금제 기본 모듈 + 이번에 추가한 모듈 + 예전에 따로 산 추가 모듈(이전 요금제 기본 모듈은 빼고)
  const oldIncluded = new Set(PLAN_MODULES[center.plan]);
  const previousAddOns = center.modules.filter((m) => !oldIncluded.has(m));
  const modules = new Set<ModuleCode>([...PLAN_MODULES[req.plan], ...req.addModules, ...previousAddOns]);
  return {
    center: { ...center, plan: req.plan, status: 'PAID', modules: MODULES.filter((m) => modules.has(m)) },
    request: { ...req, status: 'APPROVED' as const, decidedBy: by, decidedAt: now, version: req.version + 1 },
  };
}

export function rejectRequest<R extends { status: DecisionStatus; version: number }>(req: R, reason: string, by: string, now: string): R & { rejectReason: string; decidedBy: string; decidedAt: string } {
  if (req.status !== 'PENDING') fail('CONFLICT', '이미 처리된 신청입니다.');
  const r = reason.trim();
  if (r.length < 2 || r.length > 300) fail('VALIDATION', '반려 사유를 2~300자로 입력하세요.');
  return { ...req, status: 'REJECTED', rejectReason: r, decidedBy: by, decidedAt: now, version: req.version + 1 };
}

/* ---------------- 알림톡·문자 ---------------- */

export const MESSAGING_TYPES = ['SENDER_PROFILE', 'TEMPLATE', 'CREDIT_TOPUP'] as const;
export type MessagingType = (typeof MESSAGING_TYPES)[number];
/** 알림톡 템플릿에 쓸 수 있는 변수. 장애유형·진단 같은 민감정보 변수는 일부러 없다. */
export const TEMPLATE_VARIABLES = ['#{보호자명}', '#{아동명}', '#{센터명}', '#{일시}', '#{잔여회기}', '#{선생님}'] as const;
export const CREDIT_PACKS = [10_000, 30_000, 50_000, 100_000] as const;

export interface MessagingSettings {
  senderNumber?: string;
  kakaoChannelId?: string;
  profileActive: boolean;
  templates: Array<{ id: string; name: string; body: string; approvedAt: string }>;
  credits: number; // 문자·알림톡 포인트(1P = 1원)
}
export const EMPTY_MESSAGING: MessagingSettings = { profileActive: false, templates: [], credits: 0 };

export interface MessagingRequest {
  id: string;
  centerId: string;
  centerName: string;
  type: MessagingType;
  senderNumber?: string;
  kakaoChannelId?: string;
  templateName?: string;
  templateBody?: string;
  creditAmount?: number;
  status: DecisionStatus;
  requestedBy: string;
  requestedAt: string;
  decidedBy?: string;
  decidedAt?: string;
  rejectReason?: string;
  version: number;
}

const SENDER_RE = /^(0\d{1,2}-?\d{3,4}-?\d{4}|1[5-9]\d{2}-?\d{4})$/; // 일반 번호·대표번호(1588 등)

export function newMessagingRequest(i: {
  id: string; center: { id: string; name: string; modules: readonly string[]; messaging?: MessagingSettings }; type: MessagingType; by: string; now: string;
  senderNumber?: string; kakaoChannelId?: string; templateName?: string; templateBody?: string; creditAmount?: number;
}): MessagingRequest {
  const base = { id: i.id, centerId: i.center.id, centerName: i.center.name, type: i.type, status: 'PENDING' as const, requestedBy: i.by, requestedAt: i.now, version: 1 };
  if (i.type !== 'CREDIT_TOPUP' && !i.center.modules.includes('MESSAGING')) fail('FORBIDDEN', '알림톡 모듈이 없는 센터입니다. 먼저 요금제·모듈을 신청하세요.');
  if (i.type === 'SENDER_PROFILE') {
    const senderNumber = (i.senderNumber ?? '').trim();
    const kakaoChannelId = (i.kakaoChannelId ?? '').trim();
    if (!SENDER_RE.test(senderNumber)) fail('VALIDATION', '발신번호 형식을 확인하세요. 예: 02-0000-0000 또는 1588-0000');
    if (!/^@[\w가-힣.-]{2,30}$/.test(kakaoChannelId)) fail('VALIDATION', '카카오 채널 검색용 아이디는 @로 시작합니다. 예: @햇살아동발달');
    return { ...base, senderNumber, kakaoChannelId };
  }
  if (i.type === 'TEMPLATE') {
    if (!i.center.messaging?.profileActive) fail('CONFLICT', '발신프로필이 승인된 뒤에 템플릿을 신청할 수 있습니다.');
    return { ...base, ...validateTemplate(i.templateName ?? '', i.templateBody ?? '') };
  }
  const creditAmount = Number(i.creditAmount);
  if (!(CREDIT_PACKS as readonly number[]).includes(creditAmount)) fail('VALIDATION', '충전 금액을 목록에서 고르세요.');
  return { ...base, creditAmount };
}

export function validateTemplate(name: string, body: string) {
  const templateName = name.trim();
  const templateBody = body.trim();
  if (templateName.length < 2 || templateName.length > 40) fail('VALIDATION', '템플릿 이름은 2~40자로 입력하세요.');
  if (templateBody.length < 10 || templateBody.length > 1000) fail('VALIDATION', '템플릿 내용은 10~1000자로 입력하세요.');
  const unknown = (templateBody.match(/#\{[^}]*\}/g) ?? []).filter((v) => !(TEMPLATE_VARIABLES as readonly string[]).includes(v));
  if (unknown.length) fail('VALIDATION', `쓸 수 없는 변수가 있습니다: ${[...new Set(unknown)].join(', ')}. 사용 가능: ${TEMPLATE_VARIABLES.join(' ')}`);
  if (/장애|진단|병명|질환/.test(templateBody)) fail('VALIDATION', '알림톡에는 장애·진단 등 민감정보를 넣을 수 없습니다.');
  return { templateName, templateBody };
}

export function approveMessaging(settings: MessagingSettings | undefined, req: MessagingRequest, by: string, now: string) {
  if (req.status !== 'PENDING') fail('CONFLICT', '이미 처리된 신청입니다.');
  const s: MessagingSettings = { ...EMPTY_MESSAGING, ...settings, templates: [...(settings?.templates ?? [])] };
  if (req.type === 'SENDER_PROFILE') Object.assign(s, { senderNumber: req.senderNumber, kakaoChannelId: req.kakaoChannelId, profileActive: true });
  if (req.type === 'TEMPLATE') s.templates.push({ id: req.id, name: req.templateName!, body: req.templateBody!, approvedAt: now });
  if (req.type === 'CREDIT_TOPUP') s.credits += req.creditAmount!;
  return { settings: s, request: { ...req, status: 'APPROVED' as const, decidedBy: by, decidedAt: now, version: req.version + 1 } };
}

/* ---------------- 공지·문의 ---------------- */

export const NOTICE_CATEGORIES = ['MUST_READ', 'UPDATE', 'MAINTENANCE'] as const;
export type NoticeCategory = (typeof NOTICE_CATEGORIES)[number];

export interface Notice {
  id: string;
  category: NoticeCategory;
  title: string;
  body: string;
  publishedBy: string;
  publishedAt: string;
}

export function newNotice(i: { id: string; category: NoticeCategory; title: string; body: string; by: string; now: string }): Notice {
  const title = i.title.trim();
  const body = i.body.trim();
  if (!(NOTICE_CATEGORIES as readonly string[]).includes(i.category)) fail('VALIDATION', '공지 구분을 고르세요.');
  if (title.length < 2 || title.length > 120) fail('VALIDATION', '공지 제목은 2~120자로 입력하세요.');
  if (body.length < 2 || body.length > 4000) fail('VALIDATION', '공지 내용은 2~4000자로 입력하세요.');
  return { id: i.id, category: i.category, title, body, publishedBy: i.by, publishedAt: i.now };
}

export interface NoticeRead { noticeId: string; centerId: string; userId: string; at: string }

export interface Inquiry {
  id: string;
  centerId: string;
  centerName: string;
  title: string;
  body: string;
  status: 'OPEN' | 'ANSWERED';
  createdBy: string;
  createdAt: string;
  answer?: string;
  answeredBy?: string;
  answeredAt?: string;
  version: number;
}

export function newInquiry(i: { id: string; center: { id: string; name: string }; title: string; body: string; by: string; now: string }): Inquiry {
  const title = i.title.trim();
  const body = i.body.trim();
  if (title.length < 2 || title.length > 120) fail('VALIDATION', '문의 제목은 2~120자로 입력하세요.');
  if (body.length < 5 || body.length > 4000) fail('VALIDATION', '문의 내용은 5~4000자로 입력하세요.');
  return { id: i.id, centerId: i.center.id, centerName: i.center.name, title, body, status: 'OPEN', createdBy: i.by, createdAt: i.now, version: 1 };
}

export function answerInquiry(q: Inquiry, answer: string, by: string, now: string): Inquiry {
  const a = answer.trim();
  if (a.length < 2 || a.length > 4000) fail('VALIDATION', '답변은 2~4000자로 입력하세요.');
  return { ...q, status: 'ANSWERED', answer: a, answeredBy: by, answeredAt: now, version: q.version + 1 };
}
